import axios from 'axios';
import {
  ArcElement,
  BarController,
  BarElement,
  Chart,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  PolarAreaController,
  RadialLinearScale,
  ScatterController,
  TimeSeriesScale,
  Tooltip
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { createRoot } from 'react-dom/client';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/es/integration/react';
import 'chartjs-adapter-moment';

import 'typeface-roboto';

import { LoadingLogo } from '@fmbt/ui/LoadingSpinner';
import { ApplGlobalContext, applicationEventEmitter } from 'config/context/ApplicationGlobalContext';
import { initializeSentry } from 'config/sentry';
import App from 'routes/App';
import configureStore from 'store/configureStore';
import 'utils/polyfills';

import * as serviceWorker from './serviceWorker';

import './index.less';

Chart.register(
  ArcElement,
  BarController,
  BarElement,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  PolarAreaController,
  RadialLinearScale,
  ScatterController,
  TimeSeriesScale,
  Tooltip,
  annotationPlugin
);

axios.defaults.baseURL = import.meta.env.VITE_API_URL;

const {
  VITE_SENTRY_DSN,
  VITE_ENV,
  VITE_VERSION,
  VITE_GOOGLE_TAG_MANAGER_ID,
  VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID
} = import.meta.env;

// eslint-disable-next-line no-console
console.log(VITE_VERSION);

initializeSentry(
  VITE_SENTRY_DSN,
  VITE_ENV,
  VITE_VERSION
);

TagManager.initialize({
  gtmId: VITE_GOOGLE_TAG_MANAGER_ID as string
});
// Fire pageview 'GA4 - Virtual Page View' tag in GTM (prod). GTM then sends the
// data to Google Analytics for page view tracking.
TagManager.dataLayer({
  dataLayer: {
    event: 'pageview'
  }
});

const { persistor, store } = configureStore();

(async () => {
  // https://github.com/launchdarkly/react-client-sdk/issues/130#issuecomment-1656260383
  // The practical identify call is made in enterpriseScaffold/saga.ts.
  console.info('🔽 Before we receive the user detail, we use the empty context to prevent LaunchDarkly from sending out anonymous identify calls.');

  const LDProvider = await asyncWithLDProvider({
    clientSideID: VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID,
    context: {}
  });

  const container = document.getElementById('farmbot');
  const root = createRoot(container!);

  // Wrapping React.StrictMode outermost seems to get the page stuck at the <LoadingLogo />.
  // I guess PersistGate is not working well with React.StrictMode.
  // Hence I wrap React.StrictMode around <App /> instead.
  root.render(
    <Provider store={store}>
      <PersistGate loading={<LoadingLogo />} persistor={persistor}>
        <BrowserRouter>
          <LDProvider>
            <ApplGlobalContext.Provider value={{ applicationEventEmitter }}>
              <React.StrictMode>
                <App />
              </React.StrictMode>
            </ApplGlobalContext.Provider>
          </LDProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
})();

serviceWorker.unregister();
